'use client';

import { useAuth } from '@clerk/clerk-react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { apiClient } from '../api/client';
import { components } from '../api/generated/schema';

type MemberWire = components['schemas']['Member'];

export interface Member {
  id: string;
  organizationId: string;
  userId: string;
  role: 'admin' | 'member';
  status: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  isOwner: boolean;
  createdAt: string;
  updatedAt: string;
}

function transformMember(member: MemberWire): Member {
  return {
    id: member.id,
    organizationId: member.organizationId,
    userId: member.userId,
    role: member.role as 'admin' | 'member',
    status: member.status,
    email: member.email,
    firstName: member.firstName ?? null,
    lastName: member.lastName ?? null,
    isOwner: member.isOwner,
    createdAt: member.createdAt,
    updatedAt: member.updatedAt,
  };
}

// MEMBERS_PAGE_SIZE matches the backend default page size; each "load more" fetches
// the next offset-paginated window of this size.
const MEMBERS_PAGE_SIZE = 24;

// One page of the members list: the windowed rows plus the organization's grand total, so the
// load-more loop can tell when every member has been fetched.
export interface MembersResponse {
  members: Member[];
  count: number;
}

/**
 * Hook for fetching organization members
 * GET /api/v1/organization/members
 *
 * Offset-paginated "load more": each page fetches `pageSize` members at the next
 * offset and the hook accumulates them. `count` is the organization's grand total, so the loop
 * stops once every member is loaded. `pageSize` is injectable for tests (the default
 * matches the backend page size).
 */
export function useOrganizationMembers({
  pageSize = MEMBERS_PAGE_SIZE,
}: { pageSize?: number } = {}) {
  const {
    getToken,
    isLoaded,
    isSignedIn,
    orgId: clerkOrganizationId,
  } = useAuth();

  return useInfiniteQuery<MembersResponse>({
    queryKey: ['organization-members', clerkOrganizationId],
    enabled: isLoaded && isSignedIn === true && !!clerkOrganizationId,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const token = await getToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const { data, error, response } = await apiClient.GET(
        '/api/v1/organization/members',
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: { query: { limit: pageSize, offset: pageParam as number } },
        }
      );

      if (error || !response.ok || !data) {
        throw new Error('Failed to fetch organization members');
      }

      return {
        members: data.members.map(transformMember),
        count: data.count,
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.members.length, 0);
      return loaded < lastPage.count ? loaded : undefined;
    },
    staleTime: 30000,
    refetchInterval: 60000,
  });
}
